import React from "react";
import { useApp } from "@/context/AppContext";
import { StatCard } from "@/components/StatCard";
import { AddressDisplay } from "@/components/AddressDisplay";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CONSOLE } from "@/constants/testIds";
import { formatUSD, timeAgo } from "@/lib/format";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, Pause, ShieldOff } from "lucide-react";

export default function Overview() {
  const { token, audit, stats, live } = useApp();

  const supplyPct = token.maxSupply > 0n
    ? Number((token.totalSupply * 10000n) / token.maxSupply) / 100
    : 0;

  const supplySub = live
    ? (token.totalSupply === 0n
        ? "No PUSD minted yet — configure a minter, then mint."
        : (token.maxSupply > 0n ? `${supplyPct.toFixed(2)}% of cap` : "Uncapped"))
    : (token.maxSupply > 0n ? `${supplyPct.toFixed(2)}% of cap` : "Uncapped");

  const recent = audit.slice(0, 6);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{token.symbol} · {token.name}</div>
          <h1 className="text-3xl font-semibold tracking-tight">Issuance Overview</h1>
        </div>
        <div className="text-xs font-mono text-slate-500">
          Decimals <span className="text-slate-900">{token.decimals}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          testId={CONSOLE.totalSupplyStat}
          label="Total supply"
          value={`${formatUSD(token.totalSupply, token.decimals)} ${token.symbol}`}
          sub={supplySub}
          accent="emerald"
        />
        <StatCard
          testId={CONSOLE.maxSupplyStat}
          label="Max supply cap"
          value={token.maxSupply === 0n ? "Uncapped" : `${formatUSD(token.maxSupply, token.decimals)} ${token.symbol}`}
          sub="Set by Owner role"
          accent="slate"
        />
        <StatCard
          testId={CONSOLE.statusStat}
          label="Contract status"
          value={token.paused ? "PAUSED" : "ACTIVE"}
          sub={live ? "Reading on-chain state" : "Demo data — connect & configure proxy to go live"}
          accent={token.paused ? "amber" : "emerald"}
        />
      </div>

      {/* Addresses */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">Contract Addresses</h3>
          <Badge variant="outline" className="font-mono text-[10px]">UUPS Proxy</Badge>
        </div>
        <div className="divide-y divide-slate-200">
          <div className="px-5 py-4 flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Proxy</div>
            <div data-testid={CONSOLE.proxyAddress}>
              <AddressDisplay value={token.proxy} short={false} />
            </div>
          </div>
          <div className="px-5 py-4 flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Implementation</div>
            <div data-testid={CONSOLE.implAddress}>
              <AddressDisplay value={token.implementation} short={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats + recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-md p-5 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight">Compliance metrics</h3>
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Events" value={stats.events_total} />
            <Metric label="Mints" value={stats.mints} />
            <Metric label="Burns" value={stats.burns} />
            <Metric label="Blacklisted" value={stats.blacklist_active} />
            <Metric label="Role grants" value={stats.role_holders} />
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
          <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold tracking-tight">Recent activity</h3>
            <Link to="/audit" className="text-xs text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline">View full audit log →</Link>
          </div>
          <ul className="divide-y divide-slate-200">
            {recent.map((e) => (
              <li key={e.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <EventIcon type={e.event_type} />
                  <div>
                    <div className="text-sm">{e.event_type}</div>
                    <div className="text-xs font-mono text-slate-500">
                      {e.subject ? <AddressDisplay value={e.subject} short lead={6} tail={4} /> : "—"}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {e.amount ? (
                    <div className="font-mono text-sm tabular-nums">{formatUSD(BigInt(e.amount), token.decimals)} <span className="text-slate-400">{token.symbol}</span></div>
                  ) : (
                    <div className="text-xs text-slate-400 font-mono">—</div>
                  )}
                  <div className="text-[11px] text-slate-500">{timeAgo(e.timestamp)}</div>
                </div>
              </li>
            ))}
            {recent.length === 0 && (
              <li className="px-5 py-6 text-sm text-slate-500">No activity yet.</li>
            )}
          </ul>
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-white border border-slate-200 rounded-md p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">Quick actions</div>
          <div className="text-sm text-slate-600">Jump to common operator tasks.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/operations"><Button size="sm" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white"><ArrowUpRight className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />Mint</Button></Link>
          <Link to="/operations"><Button size="sm" variant="outline" className="rounded-md border-slate-200"><ArrowDownRight className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />Burn</Button></Link>
          <Link to="/compliance"><Button size="sm" variant="outline" className="rounded-md border-slate-200"><Pause className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />Pause</Button></Link>
          <Link to="/compliance"><Button size="sm" variant="outline" className="rounded-md border-slate-200"><ShieldOff className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />Blacklist</Button></Link>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">{label}</div>
      <div className="font-mono text-lg tabular-nums">{value}</div>
    </div>
  );
}

function EventIcon({ type }) {
  const map = {
    Mint: "bg-emerald-50 text-emerald-600 border-emerald-200",
    Burn: "bg-red-50 text-red-600 border-red-200",
    Blacklisted: "bg-slate-100 text-slate-900 border-slate-300",
    UnBlacklisted: "bg-slate-50 text-slate-700 border-slate-200",
    Paused: "bg-amber-50 text-amber-600 border-amber-200",
    Unpaused: "bg-emerald-50 text-emerald-600 border-emerald-200",
    RoleGranted: "bg-slate-50 text-slate-700 border-slate-200",
    RoleRevoked: "bg-slate-50 text-slate-700 border-slate-200",
    MinterConfigured: "bg-emerald-50 text-emerald-600 border-emerald-200",
    MinterRemoved: "bg-red-50 text-red-600 border-red-200",
  };
  const cls = map[type] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border rounded ${cls}`}>
      {type}
    </span>
  );
}
